import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Group,
  ScrollArea,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import {
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  Flame,
  InfoCircle,
  Search,
} from "tabler-icons-react";
import moment from "moment";
import { parseLogRecord } from "./logUtils";

const severityStyles = {
  debug: { dot: "log-dot--debug", label: "log-level--debug" },
  info: { dot: "log-dot--info", label: "log-level--info" },
  warn: { dot: "log-dot--warn", label: "log-level--warn" },
  warning: { dot: "log-dot--warn", label: "log-level--warn" },
  error: { dot: "log-dot--error", label: "log-level--error" },
  fatal: { dot: "log-dot--error", label: "log-level--error" },
};

const apiCategories = ["xhr", "fetch", "http", "request"];

function formatTimestamp(value, compact = false) {
  if (!value) return "-";
  const numeric = Number(value);
  const date =
    Number.isFinite(numeric) && `${Math.trunc(numeric)}`.length <= 10
      ? moment.unix(numeric)
      : moment(value);

  if (!date.isValid()) return "-";
  return compact ? date.format("HH:mm:ss.SSS") : date.format("MMM D, HH:mm:ss");
}

function getDuration(logData) {
  return (
    logData.duration ||
    logData.duration_ms ||
    logData.elapsed_ms ||
    logData.data?.duration ||
    logData.data?.duration_ms ||
    logData.data?.elapsed_ms ||
    ""
  );
}

function getStatus(logData) {
  return (
    logData.status ||
    logData.status_code ||
    logData.data?.status ||
    logData.data?.status_code ||
    ""
  );
}

function getRequest(logData) {
  return (
    logData.request ||
    logData.request_id ||
    logData.traceId ||
    logData.trace_id ||
    logData.data?.request_id ||
    logData.data?.method ||
    "-"
  );
}

function getMessage(logData) {
  if (apiCategories.includes(logData.category) && logData.data?.url) {
    const method = logData.data?.method ? `${logData.data.method} ` : "";
    return `${method}${logData.data.url}`;
  }
  return logData.message || logData.event || logData.name || "No message";
}

function normalizeLog(item, index) {
  const logData = parseLogRecord(item);
  const level = (logData.level || logData.severity || "info").toLowerCase();
  const category = logData.category || logData.service || "app";
  const status = getStatus(logData);

  return {
    id: `${logData.timestamp || "log"}-${index}`,
    raw: logData,
    time: formatTimestamp(logData.timestamp, true),
    sortTime: logData.timestamp || 0,
    level,
    category,
    request: getRequest(logData),
    message: getMessage(logData),
    status,
    duration: getDuration(logData),
  };
}

function normalizeSession(session, index) {
  const started = session.received || session.firstLogTimestamp;
  const lastSeen = session.lastLogTimestamp || session.received;
  const startMoment = started ? moment(started) : null;
  const endMoment = lastSeen ? moment(lastSeen) : null;
  const duration =
    startMoment?.isValid() && endMoment?.isValid()
      ? moment.duration(endMoment.diff(startMoment)).humanize()
      : "-";

  return {
    id: `${session.session_id || "session"}-${index}`,
    raw: session,
    started,
    startedLabel: formatTimestamp(started),
    lastSeenLabel: formatTimestamp(lastSeen),
    sessionId: session.session_id || session.sessionId || "-",
    logCount: session.logCount || 0,
    duration,
  };
}

function includesSearch(row, search) {
  if (!search.trim()) return true;
  const query = search.toLowerCase().trim();
  return Object.values(row).some((value) => {
    if (value == null || typeof value === "object") return false;
    return String(value).toLowerCase().includes(query);
  });
}

function compareValues(a, b, reversed) {
  const aValue = a ?? "";
  const bValue = b ?? "";
  if (typeof aValue === "number" && typeof bValue === "number") {
    return reversed ? bValue - aValue : aValue - bValue;
  }
  return reversed
    ? String(bValue).localeCompare(String(aValue))
    : String(aValue).localeCompare(String(bValue));
}

function SortHeader({ field, sortBy, reversed, onSort, children, align }) {
  const active = sortBy === field;
  const Icon = active && reversed ? ChevronUp : ChevronDown;

  return (
    <th className={align === "right" ? "logs-table__head--right" : ""}>
      <button
        type="button"
        className={`logs-table__sort ${active ? "logs-table__sort--active" : ""}`}
        onClick={() => onSort(field)}
      >
        <span>{children}</span>
        <Icon size={12} />
      </button>
    </th>
  );
}

function SeverityMark({ level }) {
  const styles = severityStyles[level] || severityStyles.info;
  return (
    <span className="log-level">
      <span className={`log-dot ${styles.dot}`} />
      <span className={styles.label}>{level}</span>
    </span>
  );
}

function MessageIcon({ level }) {
  if (level === "error" || level === "fatal") {
    return <AlertCircle size={14} className="log-message__icon log-message__icon--error" />;
  }
  if (level === "warn" || level === "warning") {
    return <Flame size={14} className="log-message__icon log-message__icon--warn" />;
  }
  if (level === "info") {
    return <InfoCircle size={14} className="log-message__icon log-message__icon--info" />;
  }
  return null;
}

export function TableSort({ data, onPress, filterBy, viewType, userId }) {
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  const [activeRowId, setActiveRowId] = useState(null);
  const isSessionView = viewType === "sessions" || filterBy === "user_sessions";

  useEffect(() => {
    setActiveRowId(null);
  }, [data, viewType, filterBy]);

  const rows = useMemo(() => {
    const normalized = isSessionView
      ? data.map(normalizeSession)
      : data.map(normalizeLog);

    const filtered = normalized.filter((row) => includesSearch(row, search));
    if (!sortBy) return filtered;

    return [...filtered].sort((a, b) =>
      compareValues(a[sortBy], b[sortBy], reverseSortDirection)
    );
  }, [data, isSessionView, reverseSortDirection, search, sortBy]);

  const setSorting = (field) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
  };

  const buildSessionHref = (sessionId) => {
    if (!userId || !sessionId || sessionId === "-") return null;
    const params = new URLSearchParams({ userId, sessionId });
    return `/?${params.toString()}`;
  };

  const handleSessionClick = (event, row) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    event.preventDefault();
    onPress(row.raw);
  };

  const handleLogClick = (row) => {
    setActiveRowId(row.id);
    onPress(row.raw);
  };

  return (
    <section className="logs-panel">
      <div className="logs-panel__toolbar">
        <div>
          <Text className="logs-panel__eyebrow">
            {isSessionView ? "Sessions" : "Session stream"}
          </Text>
          <Text className="logs-panel__title">
            {isSessionView ? "User sessions" : "Logs"}
          </Text>
        </div>
        <TextInput
          className="logs-panel__search"
          placeholder={
            isSessionView
              ? "Search sessions"
              : "level:error category:http request:..."
          }
          icon={<Search size={14} />}
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          rightSection={<span className="kbd-label">/</span>}
        />
      </div>

      <ScrollArea className="logs-panel__scroll">
        <Table className={isSessionView ? "logs-table sessions-table" : "logs-table stream-table"}>
          {isSessionView ? (
            <colgroup>
              <col className="session-col-started" />
              <col className="session-col-last" />
              <col className="session-col-id" />
              <col className="session-col-count" />
              <col className="session-col-duration" />
            </colgroup>
          ) : (
            <colgroup>
              <col className="stream-col-time" />
              <col className="stream-col-level" />
              <col className="stream-col-category" />
              <col className="stream-col-request" />
              <col className="stream-col-message" />
            </colgroup>
          )}
          {isSessionView ? (
            <thead>
              <tr>
                <SortHeader field="startedLabel" sortBy={sortBy} reversed={reverseSortDirection} onSort={setSorting}>
                  Started
                </SortHeader>
                <SortHeader field="lastSeenLabel" sortBy={sortBy} reversed={reverseSortDirection} onSort={setSorting}>
                  Last seen
                </SortHeader>
                <SortHeader field="sessionId" sortBy={sortBy} reversed={reverseSortDirection} onSort={setSorting}>
                  Session
                </SortHeader>
                <SortHeader field="logCount" sortBy={sortBy} reversed={reverseSortDirection} onSort={setSorting}>
                  Logs
                </SortHeader>
                <SortHeader field="duration" sortBy={sortBy} reversed={reverseSortDirection} onSort={setSorting}>
                  Duration
                </SortHeader>
              </tr>
            </thead>
          ) : (
            <thead>
              <tr>
                <SortHeader field="time" sortBy={sortBy} reversed={reverseSortDirection} onSort={setSorting}>
                  Time
                </SortHeader>
                <SortHeader field="level" sortBy={sortBy} reversed={reverseSortDirection} onSort={setSorting}>
                  Lvl
                </SortHeader>
                <SortHeader field="category" sortBy={sortBy} reversed={reverseSortDirection} onSort={setSorting}>
                  Category
                </SortHeader>
                <SortHeader field="request" sortBy={sortBy} reversed={reverseSortDirection} onSort={setSorting}>
                  Request
                </SortHeader>
                <SortHeader field="message" sortBy={sortBy} reversed={reverseSortDirection} onSort={setSorting}>
                  Message
                </SortHeader>
              </tr>
            </thead>
          )}
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={isSessionView ? 5 : 5}>
                  <Box className="logs-empty">
                    <Clock size={18} />
                    <Text>No matching rows</Text>
                  </Box>
                </td>
              </tr>
            ) : isSessionView ? (
              rows.map((row) => {
                const href = buildSessionHref(row.sessionId);
                return (
                  <tr key={row.id} className="logs-table__row">
                    <td className="tabular-muted">
                      <a
                        href={href || "#"}
                        className="row-link-cover"
                        onClick={(event) => handleSessionClick(event, row)}
                        onAuxClick={(event) => handleSessionClick(event, row)}
                      >
                        {row.startedLabel}
                      </a>
                    </td>
                    <td className="tabular-muted truncate-cell">{row.lastSeenLabel}</td>
                    <td className="truncate-cell">
                      <span className="mono-strong">{row.sessionId}</span>
                    </td>
                    <td>
                      <Badge className="compact-badge">{row.logCount} logs</Badge>
                    </td>
                    <td className="tabular-muted truncate-cell">{row.duration}</td>
                  </tr>
                );
              })
            ) : (
              rows.map((row) => (
                <tr
                  key={row.id}
                  className={`logs-table__row ${
                    row.level === "error" || row.level === "fatal"
                      ? "logs-table__row--error"
                      : ""
                  } ${activeRowId === row.id ? "logs-table__row--active" : ""}`}
                  onClick={() => handleLogClick(row)}
                >
                  <td className="tabular-muted">{row.time}</td>
                  <td>
                    <SeverityMark level={row.level} />
                  </td>
                  <td className="tabular-muted truncate-cell">{row.category}</td>
                  <td className="tabular-muted truncate-cell">{row.request}</td>
                  <td className="logs-message-cell">
                    <span className="log-message">
                      <MessageIcon level={row.level} />
                      <Tooltip label={row.message} disabled={row.message.length < 72}>
                        <span className="log-message__text">{row.message}</span>
                      </Tooltip>
                      {row.status ? (
                        <Badge
                          className={`status-badge ${
                            Number(row.status) >= 400 ? "status-badge--error" : ""
                          }`}
                        >
                          {row.status}
                        </Badge>
                      ) : null}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </ScrollArea>

      <Group position="apart" className="logs-panel__footer">
        <Text>
          Showing {rows.length} {isSessionView ? "sessions" : "entries"}
        </Text>
        <Text>{isSessionView ? "click a row to inspect logs" : "click a row for details"}</Text>
      </Group>
    </section>
  );
}
